import React, {
  cloneElement,
  isValidElement,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DatePicker as AntDatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import enUS from 'antd/es/date-picker/locale/en_US';
import fiFI from 'antd/es/date-picker/locale/fi_FI';
import svSE from 'antd/es/date-picker/locale/sv_SE';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fi';
import 'dayjs/locale/sv';
import { useTranslation } from 'react-i18next';
import { getRegionLocale } from '../../../constants/locale';
import { buildRegionalDateFormat, getKeyboardTargetDate } from '../../../helpers/datePickerHelper';
import {
  describePickerCell,
  getPanelCellTarget,
  getPickerFooterStops,
  getPickerHeaderStops,
  getRowEdgeCellKey,
  isPickerGridMode,
  keepActiveDateInView,
  parsePickerCellKey,
  PICKER_CELL_ATTRIBUTE,
  pickerCellKey,
  PickerGridMode,
} from '../../../helpers/pickerPopupHelper';
import { takeOverKey, usePickerPopupKeyboard } from '../../../hooks/usePickerPopupKeyboard';
import { useDatePickerInputEvents } from './useDatePickerInputEvents';
import { useLanguage } from '../../../context/LanguageContext';
import { CalendarOutline } from '../../icons';
import styled from 'styled-components';

const withMonthNames = (locale: typeof enUS): typeof enUS => ({
  ...locale,
  lang: { ...locale.lang, monthFormat: 'MMM' },
});

const PICKER_LOCALES: Record<string, typeof enUS> = {
  en: withMonthNames(enUS),
  fi: withMonthNames(fiFI),
  sv: withMonthNames(svSE),
};

export const StyledDatePicker = styled(AntDatePicker)`
  .ant-picker-input > input {
    line-height: 20px;
  }
  .picker-popup-trigger {
    display: flex;
    padding: 0;
    border: 0;
    border-radius: 2px;
    background: none;
    color: inherit;
  }
` as typeof AntDatePicker;

/** rc-picker commits typed text itself when the field closes; a commit this soon after ours is that echo. */
const DUPLICATE_WINDOW_MS = 200;

const isActivationKey = (key: string) => key === 'Enter' || key === ' ';

const CustomDatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder,
  format: formatProp,
  open: openProp,
  onOpenChange,
  suffixIcon,
  ...restProps
}) => {
  const { t } = useTranslation();
  const regionLocale = getRegionLocale();
  const regionalFormat = useMemo(() => buildRegionalDateFormat(regionLocale), [regionLocale]);
  const format = formatProp ?? regionalFormat;
  const { language } = useLanguage();
  const pickerLocale = PICKER_LOCALES[language] ?? PICKER_LOCALES.en;
  const pickerRef = useRef<React.ComponentRef<typeof AntDatePicker>>(null);
  const [viewedMonth, setViewedMonth] = useState<Dayjs>(value ?? dayjs());
  const [gridMode, setGridMode] = useState<PickerGridMode>('date');
  const [keyboardDate, setKeyboardDate] = useState<Dayjs | null>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const lastTypedCommit = useRef<{ date: Dayjs; at: number } | null>(null);
  const isOpen = openProp ?? internalOpen;
  const isCurrentMonth = viewedMonth.isSame(dayjs(), 'month');
  const activeDate = keepActiveDateInView(keyboardDate ?? value ?? dayjs(), viewedMonth, gridMode);

  const emitChange: NonNullable<DatePickerProps['onChange']> = (date, dateStr) => {
    // If the user selected today, force the viewed month to today as well
    if (date && dayjs().isSame(date, 'day')) {
      setViewedMonth(dayjs());
    }
    onChange?.(date, dateStr);
  };

  const handleChange: NonNullable<DatePickerProps['onChange']> = (date, dateStr) => {
    const lastTyped = lastTypedCommit.current;
    const repeatsTypedCommit =
      date &&
      lastTyped?.date.isSame(date, 'day') &&
      Date.now() - lastTyped.at < DUPLICATE_WINDOW_MS;
    if (repeatsTypedCommit) return;
    emitChange(date, dateStr);
  };

  const applyOpen = (nextOpen: boolean) => {
    setInternalOpen(nextOpen);
    if (!nextOpen) {
      setGridMode('date');
      setKeyboardDate(null);
    }
    onOpenChange?.(nextOpen);
  };

  useEffect(() => {
    if (value) setViewedMonth(value);
  }, [value]);

  /** The limits the calendar applies, so neither typing nor the arrow keys can get past them. */
  const isSelectableDate = (date: Dayjs) =>
    !restProps.disabledDate?.(date, { type: 'date' }) &&
    !(restProps.minDate && date.isBefore(restProps.minDate, 'day')) &&
    !(restProps.maxDate && date.isAfter(restProps.maxDate, 'day'));

  const parseTypedDate = (text: string) => {
    if (typeof format !== 'string') return undefined;
    const typedDate = dayjs(text.trim(), format, true);
    return typedDate.isValid() ? typedDate : undefined;
  };

  const commitTypedText = (text: string) => {
    const typedDate = parseTypedDate(text);
    if (!typedDate || typedDate.isSame(value, 'day') || !isSelectableDate(typedDate)) return;
    lastTypedCommit.current = { date: typedDate, at: Date.now() };
    emitChange(typedDate, text.trim());
  };

  /** An empty field starts from today rather than a day either side of it. */
  const stepDay = (typedText: string, days: 1 | -1) => {
    const from = parseTypedDate(typedText) ?? value;
    const next = from ? from.add(days, 'day') : dayjs().startOf('day');
    if (!isSelectableDate(next)) return;
    emitChange(next, typeof format === 'string' ? next.format(format) : next.format());
  };

  const findActiveCell = (popup: HTMLElement) =>
    popup.querySelector<HTMLElement>(
      `[${PICKER_CELL_ATTRIBUTE}="${pickerCellKey(activeDate, gridMode)}"]`,
    );

  const nextKeyboardDate = (cell: HTMLElement, event: React.KeyboardEvent) => {
    if (gridMode !== 'date') return getPanelCellTarget(activeDate, event.key, gridMode);
    const rowEdge = getRowEdgeCellKey(cell, event.key);
    if (rowEdge) return parsePickerCellKey(rowEdge);
    return getKeyboardTargetDate(activeDate, event.key, event.shiftKey);
  };

  const handleControlKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    focusActiveControlAfterRender: () => void,
  ) => {
    const target = event.target as HTMLElement;
    const isActivation = isActivationKey(event.key);
    const viewSwitch = target.closest<HTMLElement>('.ant-picker-header-view button');
    if (viewSwitch && isActivation) {
      // Switching to the month or year view removes the button that had focus.
      takeOverKey(event);
      focusActiveControlAfterRender();
      viewSwitch.click();
      return;
    }

    const cell = target.closest<HTMLElement>(`[${PICKER_CELL_ATTRIBUTE}]`);
    if (!cell) {
      if (isActivation && target instanceof HTMLAnchorElement) {
        takeOverKey(event);
        target.click();
      }
      return;
    }
    if (isActivation) {
      takeOverKey(event);
      if (gridMode !== 'date') focusActiveControlAfterRender();
      cell.click();
      return;
    }

    const next = nextKeyboardDate(cell, event);
    if (!next) return;
    takeOverKey(event);
    setKeyboardDate(next);
    setViewedMonth(next);
    focusActiveControlAfterRender();
  };

  const popupKeyboard = usePickerPopupKeyboard({
    isOpen,
    setOpen: applyOpen,
    label: t('transaction:chooseDate'),
    getInput: () => pickerRef.current?.nativeElement?.querySelector('input'),
    getActiveControl: findActiveCell,
    getTabStops: (popup) =>
      [
        ...getPickerHeaderStops(popup),
        findActiveCell(popup),
        ...getPickerFooterStops(popup),
      ].filter((stop): stop is HTMLElement => !!stop),
    onControlKeyDown: handleControlKeyDown,
  });

  const handleOpenChange = (nextOpen: boolean) => {
    popupKeyboard.noteOpenChange(nextOpen);
    applyOpen(nextOpen);
  };

  const focusCalendar = () => {
    if (!isOpen) {
      setKeyboardDate(null);
      setViewedMonth(value ?? dayjs());
    }
    popupKeyboard.focusInside();
  };

  useDatePickerInputEvents(pickerRef, {
    isOpen,
    showsCommittedValue: (input) =>
      input.value === (value && typeof format === 'string' ? value.format(format) : ''),
    focusCalendar,
    closeCalendar: () => applyOpen(false),
    commitTypedText,
    stepDay,
  });

  const renderCell: DatePickerProps['cellRender'] = (current, info) => {
    const mode = info.type;
    if (!dayjs.isDayjs(current) || !isPickerGridMode(mode) || !isValidElement(info.originNode)) {
      return info.originNode;
    }
    return cloneElement(info.originNode as ReactElement<Record<string, unknown>>, {
      [PICKER_CELL_ATTRIBUTE]: pickerCellKey(current, mode),
      tabIndex: -1,
      role: 'button',
      'aria-label': describePickerCell(current, mode, language),
      'aria-current': mode === 'date' && current.isSame(dayjs(), 'day') ? 'date' : undefined,
    });
  };

  const handlePanelChange: DatePickerProps['onPanelChange'] = (date, mode) => {
    setViewedMonth(date);
    if (isPickerGridMode(mode)) setGridMode(mode);
  };

  return (
    <StyledDatePicker
      {...restProps}
      ref={pickerRef}
      format={format}
      value={value}
      open={isOpen}
      onOpenChange={handleOpenChange}
      onChange={handleChange}
      onPanelChange={handlePanelChange}
      pickerValue={viewedMonth}
      cellRender={renderCell}
      panelRender={popupKeyboard.panelRender}
      suffixIcon={
        <button
          type="button"
          className="picker-popup-trigger"
          aria-label={t('transaction:chooseDate')}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          disabled={restProps.disabled}
          onClick={(event) => {
            event.stopPropagation();
            focusCalendar();
          }}
        >
          {suffixIcon ?? <CalendarOutline className="calendar-icon" />}
        </button>
      }
      {...(!isCurrentMonth ? { showNow: true } : { showNow: false })}
      placeholder={placeholder}
      locale={pickerLocale}
    />
  );
};

export default CustomDatePicker;
